<svelte:options
	customElement={{
		tag: 'rf-book-reader',
		shadow: 'open',
	}}
/>

<script module>
	import { Vault } from '@iiif/helpers';

	type BookReaderEl = HTMLElement & { vault: Vault; whenReady: (callback: () => void) => void };

	/**
	 * Props
	 */
	export type ReframeBookReaderProps = {
		id: string;
		tag: string;
		'manifest-id': string;
		'show-thumbnails': boolean;
		'text-enabled': true;
		'items-per-page': number;
		'alone-cover': boolean;
		'reading-direction': 'left-to-right' | 'right-to-left';
		'use-manifest-direction': boolean;
		'thumbnail-placeholder': string | undefined;
		layout: 'horizontal' | 'vertical';
		align: 'top' | 'center' | 'bottom';
		margin: number;
		previousLabel: string;
		nextLabel: string;
		'no-collection': boolean;
	};
</script>

<script lang="ts">
	/**
	 * Imports
	 */
	import '@digirati/canvas-panel-web-components';
	import { onMount, tick } from 'svelte';
	import { get } from 'svelte/store';
	import { useSlottedElements } from '@src/composables/useSlottedElements';
	import type { ManifestNormalized } from '@iiif/presentation-3-normalized';
	import classNames from 'classnames';
	import { v4 as uuidv4 } from 'uuid';
	import { useCollection } from '@src/composables/useCollection.svelte.ts';
	import parseBool from '@src/utils/bool';

	/**
	 * Constants
	 */
	const name = 'rf-book-reader';
	const componentId = uuidv4();
	/**
	 * Props
	 */
	let {
		id = '',
		tag = 'section',
		'manifest-id': manifestId = '',
		'show-thumbnails': showThumbnails = true,
		'text-enabled': textEnabled = true,
		'items-per-page': itemsPerPage = 3,
		'alone-cover': aloneCover = true,
		'reading-direction': readingDirection = 'left-to-right',
		'use-manifest-direction': useManifestDirection = true,
		'thumbnail-placeholder': thumbnailPlaceholder = undefined,
		layout = 'horizontal',
		align = 'center',
		previousLabel = 'Previous',
		nextLabel = 'Next',
		margin = 20,
		'no-collection': noCollection = false,
	}: ReframeBookReaderProps = $props();

	/**
	 * States
	 */
	let bookReaderEl: BookReaderEl | null = $state(null);
	let manifest: ManifestNormalized | undefined = $state(undefined);
	let canvases: { id: string; src: string; label?: string | undefined; index: number; width: number; height: number }[] = $state([]);
	let activeThumbnails: number[] = $state([0]);
	let thumbnailsContainer: HTMLDivElement | null = $state(null);
	let sequences: number[][] = $state([]);
	let currentSequence: number = $state(-1);
	let direction: 'left-to-right' | 'right-to-left' = $state(readingDirection);

	/**
	 * Derived
	 */
	const baseSequenceIndex = $derived(aloneCover ? 1 : 0);

	/**
	 * Composables
	 */
	const { element } = useSlottedElements();

	export const collection = parseBool(noCollection)
		? undefined
		: useCollection(id, {
				element,
				moduleName: name,
				componentId,
			});

	/**
	 * Methods
	 */
	const handlePage = (direction: 'next' | 'previous') => {
		if (bookReaderEl) {
			if (direction === 'next' && currentSequence < sequences.length - 1) {
				currentSequence++;
			} else if (direction === 'previous' && currentSequence > 0) {
				currentSequence--;
			}
		}
	};

	const handleThumbnailClick = (index: number) => {
		const sequenceIndex = sequences.findIndex((sequence) => sequence.includes(index));
		if (sequenceIndex !== -1) {
			currentSequence = sequenceIndex;
		}
	};

	const scrollToActiveThumbnail = () => {
		if (thumbnailsContainer && activeThumbnails.length > 0) {
			const activeIndex = activeThumbnails[activeThumbnails.length - 1];
			const thumbnailElement = thumbnailsContainer.children[activeIndex] as HTMLElement;
			if (thumbnailElement) {
				thumbnailElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
			}
		}
	};

	const calculateSequenceXs = () => {
		// Calculate the x position of each canvas in the sequence
		const sequenceXs = [];
		if (layout === 'horizontal') {
			for (let i = 0; i < sequences[currentSequence].length; i++) {
				const sequenceWidth = [];
				for (let j = 0; j < i; j++) {
					sequenceWidth.push(canvases[sequences[currentSequence][j]].width);
				}
				sequenceXs.push(sequenceWidth.reduce((acc, width) => acc + width, 0));
			}
		} else if (layout === 'vertical') {
			for (let i = 0; i < sequences[currentSequence].length; i++) {
				sequenceXs.push(0);
			}
		}
		return sequenceXs;
	};

	const calculateSequenceYs = () => {
		// Calculate the y position of each canvas in the sequence
		const sequenceYs = [];
		if (layout === 'horizontal') {
			const tallestCanvasInCurrentSequence = Math.max(
				...canvases.filter((canvas) => sequences[currentSequence].includes(canvas.index)).map((canvas) => canvas.height)
			);
			for (let i = 0; i < sequences[currentSequence].length; i++) {
				const currentCanvasHeight = canvases[sequences[currentSequence][i]].height;
				const difference = tallestCanvasInCurrentSequence - currentCanvasHeight;
				if (align === 'center') {
					sequenceYs.push(difference / 2);
				} else if (align === 'bottom') {
					sequenceYs.push(difference);
				} else if (align === 'top') {
					sequenceYs.push(0);
				}
			}
		}
		if (layout === 'vertical') {
			for (let i = 0; i < sequences[currentSequence].length; i++) {
				const sequenceHeight = [];
				for (let j = 0; j < i; j++) {
					sequenceHeight.push(canvases[sequences[currentSequence][j]].height);
				}
				sequenceYs.push(sequenceHeight.reduce((acc, height) => acc + height, 0));
			}
		}
		return sequenceYs;
	};

	const updateSequences = () => {
		const totalCanvases = canvases.length;
		const newSequences: number[][] = [];

		if (totalCanvases > 0) {
			// Add first canvas as single sequence if aloneCover is true
			if (aloneCover) {
				newSequences.push([0]);
			}
			// Group middle canvases by itemsPerView
			for (let i = baseSequenceIndex; i < totalCanvases - baseSequenceIndex; i += itemsPerPage) {
				const sequence = [];
				for (let j = 0; j < itemsPerPage && i + j < totalCanvases - baseSequenceIndex; j++) {
					sequence.push(i + j);
				}
				if (sequence.length > 0) {
					newSequences.push(sequence);
				}
			}
			// Add last canvas as single sequence if there's more than one canvas and aloneCover is true
			if (totalCanvases > 1 && aloneCover) {
				newSequences.push([totalCanvases - 1]);
			}
		}

		sequences = newSequences;
	};

	const initReader = async (vault: Vault) => {
		if (!bookReaderEl) return;
		manifest = (await vault.loadManifest(manifestId)) as ManifestNormalized;
		// Get the manifest viewing direction
		const manifestViewingDirection = manifest?.viewingDirection;
		// If the manifest viewing direction is not set, use the reading direction prop
		if (useManifestDirection && manifestViewingDirection) {
			if (manifestViewingDirection === 'right-to-left' || manifestViewingDirection === 'left-to-right') {
				direction = manifestViewingDirection;
			}
		}
		// Set the direction of the reader based on the manifest
		bookReaderEl.whenReady(async () => {
			await tick();
			if (direction === 'right-to-left') {
				currentSequence = sequences.length - 1;
			} else if (direction === 'left-to-right') {
				currentSequence = 0;
			}
		});
		// Set the canvases data
		canvases = vault.get(manifest?.items).map((item, i) => {
			const canvas = vault.get(item);
			// Get the label of the canvas, default to the index
			let label = direction === 'right-to-left' ? (i + 1).toString() : (canvases?.length - i).toString();
			if (canvas.label) {
				label =
					Object.keys(canvas.label)
						.map((key) => canvas.label?.[key] || null)
						.filter((label) => label !== null)[0]
						.join('') || i.toString();
			}

			return {
				id: item.id,
				src: canvas.thumbnail[0]?.id,
				label,
				index: i,
				width: canvas.width,
				height: canvas.height,
			};
		});
	};

	/**
	 * Effects
	 */
	$effect(() => {
		if (canvases.length > 0) {
			updateSequences();
		}
	});

	$effect(() => {
		if (sequences.length > 0 && currentSequence >= 0) {
			activeThumbnails = sequences[currentSequence];
		}
	});

	$effect(() => {
		if (activeThumbnails.length > 0) {
			scrollToActiveThumbnail();
		}
	});

	/**
	 * Lifecycle
	 */
	onMount(() => {
		bookReaderEl = get(element) as BookReaderEl | null;
		if (!bookReaderEl) return;
		const vault = bookReaderEl?.vault;

		if (vault) {
			initReader(vault);
		}
	});
</script>

<slot name="header"></slot>
<svelte:element this={tag} class="rf-book-reader">
	<div class="rf-book-reader__container">
		{#key currentSequence}
			<layout-container preset="zoom" bind:this={$element}>
				{#each sequences[currentSequence] as canvas, i}
					<canvas-panel
						nested
						text-enabled={textEnabled}
						manifest-id={manifestId}
						canvas-id={canvases[canvas].id}
						x={calculateSequenceXs()[i] + (layout === 'horizontal' ? margin * i : 0)}
						y={calculateSequenceYs()[i] + (layout === 'vertical' ? margin * i : 0)}
					></canvas-panel>
				{/each}
			</layout-container>
		{/key}
		<div class="rf-book-reader__controls">
			<button
				class="rf-book-reader__control rf-book-reader__control__prev"
				disabled={currentSequence === 0}
				onclick={() => handlePage('previous')}
			>
				{previousLabel}
			</button>
			<button
				class="rf-book-reader__control rf-book-reader__control__next"
				disabled={currentSequence === sequences.length - 1}
				onclick={() => handlePage('next')}
			>
				{nextLabel}
			</button>
		</div>
	</div>
	{#if parseBool(showThumbnails) && currentSequence >= 0}
		<div class="rf-book-reader__thumbnails" bind:this={thumbnailsContainer}>
			{#each canvases as canvas, i}
				<button
					class={classNames('rf-book-reader__thumbnail', { 'rf-book-reader__thumbnail--active': activeThumbnails.includes(i) })}
					onclick={() => handleThumbnailClick(canvas.index)}
				>
					{#if canvas.src}
						<img src={canvas.src} alt={canvas.label} />
					{:else if !thumbnailPlaceholder}
						<div class="rf-book-reader__thumbnail__placeholder"></div>
					{:else}
						<img src={thumbnailPlaceholder} alt={canvas.label} />
					{/if}
					<span class={classNames('rf-book-reader__thumbnail__label', { 'rf-book-reader__thumbnail__label--no-thumbnail': !canvas.src })}>
						{canvas.label}
					</span>
				</button>
			{/each}
		</div>
	{/if}
</svelte:element>
<slot name="footer"></slot>

<style lang="scss" global>
	.rf-book-reader {
		&__container {
			position: relative;
		}

		&__controls {
			position: absolute;
			transform: translateY(-50%);
			top: 50%;
			z-index: 10;
			display: flex;
			justify-content: space-between;
			width: 100%;
			padding: var(--rf-spacing-s, 8px);
			box-sizing: border-box;
		}
		&__control {
			all: unset;
			width: var(--rf-spacing-xl, 40px);
			height: var(--rf-spacing-xl, 40px);
			color: transparent;
			background: var(--rf-main-background-color, white);
			cursor: pointer;
			transition: opacity 0.5s ease;
			border-radius: 50%;
			position: relative;
			user-select: none;
			&[disabled] {
				opacity: 0.5;
				cursor: not-allowed;
				&:hover {
					opacity: 0.5;
				}
			}
			&:hover {
				opacity: 0.8;
			}
			&::before {
				content: '←';
				display: flex;
				justify-content: center;
				align-items: center;
				color: var(--rf-primary-font-color, black);
				line-height: calc(var(--rf-spacing-xl, 40px) - 1px);
			}
			&__next {
				&::before {
					content: '→';
				}
			}
		}
		&__thumbnails {
			width: 100%;
			overflow-x: auto;
			display: flex;
			gap: var(--rf-spacing-xs, 4px);
			margin-top: var(--rf-spacing-s, 8px);
		}

		&__thumbnail {
			width: var(--rf-book-reader-thumbnail-size, 64px);
			flex-grow: 0;
			flex-shrink: 0;
			display: flex;
			flex-direction: column;
			gap: var(--rf-spacing-s, 8px);
			padding: 0;
			margin: 0;
			position: relative;
			border: 1px solid #000;
			appearance: none;
			cursor: pointer;
			height: auto;
			background-color: transparent;
			&__placeholder {
				width: 100%;
				min-height: 64px;
				box-shadow: 0 0 0 1px #000;
				background-color: #ffe6af;
				position: relative;
				display: flex;
				flex-direction: column;
				align-items: center;
				justify-content: center;
				&::after {
					content: 'No thumbnail';
					font-size: 12px;
				}
			}
			&:hover {
				opacity: 0.8;
			}
			&--active {
				opacity: 1;
				border: 3px solid #000;
				cursor: initial;
				&:hover {
					opacity: 1;
				}
			}
			img {
				width: 100%;
				height: 100%;
				object-fit: cover;
			}
			&__label {
				position: absolute;
				bottom: 0;
				right: 0;
				text-align: center;
				background-color: #fff;
				padding: 0 var(--rf-spacing-xs, 4px);
				font-size: 12px;
				&--no-thumbnail {
					position: relative;
					padding: var(--rf-spacing-xs, 4px);
				}
			}
		}
	}
</style>
